import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CSVUploadModal from '../components/CSVUploadModal';

// ── Extract digits only: "BTL4/4566/S2024" -> "44566"
const extractAdminNumberDigits = (adminNumber) => {
  if (!adminNumber) return '';
  const match = String(adminNumber).match(/\d+/g);
  return match ? match.join('') : '';
};

// ── Uppercase + trim
const normalizeAdminNumber = (adminNumber) =>
  adminNumber ? String(adminNumber).trim().toUpperCase() : '';

// ── Level label for a course
const getCourseLevelLabel = (course) => {
  if (!course) return '';
  if (course.level_number) return `Level ${course.level_number}`;
  if (course.level_name) return course.level_name;
  return '';
};

// ── Full display (used in tables and tooltips)
const getCourseDisplay = (course) => {
  if (!course) return '';
  const lvl = getCourseLevelLabel(course);
  return lvl ? `${course.name} (${lvl})` : course.name;
};

// ═══════════════════════════════════════════════════════════
// Custom searchable Course dropdown
// - Each option shows: Course Name on line 1, Level on line 2
// - Long names wrap naturally (no truncation)
// - Search box to filter (useful with many courses)
// ═══════════════════════════════════════════════════════════
const CourseSelect = ({
  courses,
  value,
  onChange,
  disabled,
  placeholder = '-- Select Course --'
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => courses.find((c) => String(c.id) === String(value)) || null,
    [courses, value]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return courses;
    const q = query.toLowerCase();
    return courses.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        String(getCourseLevelLabel(c)).toLowerCase().includes(q)
    );
  }, [courses, query]);

  const handleSelect = (course) => {
    onChange(String(course.id));
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`input-field w-full min-h-[42px] h-auto px-3 py-2 text-left flex items-center justify-between gap-2 ${
          disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''
        }`}
      >
        <span className="flex-1 min-w-0 whitespace-normal break-words leading-snug">
          {selected ? (
            <>
              <span className="block text-sm text-gray-900">
                {selected.name}
              </span>
              {getCourseLevelLabel(selected) && (
                <span className="block text-xs text-gray-500 mt-0.5">
                  {getCourseLevelLabel(selected)}
                </span>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {selected && !disabled && (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-700 text-lg leading-none px-1"
              title="Clear"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleClear(e);
              }}
            >
              ×
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>

      {/* Dropdown panel */}
      {open && !disabled && (
        <div className="absolute z-[60] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search course..."
              className="w-full h-9 px-3 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Options */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No courses match “{query}”
              </div>
            ) : (
              filtered.map((course) => {
                const isSelected = String(course.id) === String(value);
                return (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => handleSelect(course)}
                    className={`w-full text-left px-3 py-2 flex flex-col gap-0.5 hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <span className="text-sm text-gray-900 whitespace-normal break-words leading-snug">
                      {course.name}
                    </span>
                    {getCourseLevelLabel(course) && (
                      <span className="text-xs text-gray-500">
                        {getCourseLevelLabel(course)}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Graduates = () => {
  const { user } = useAuth();
  const [graduates, setGraduates] = useState([]);
  const [filteredGraduates, setFilteredGraduates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [examBodies, setExamBodies] = useState([]);
  const [graduationDays, setGraduationDays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [editingGraduate, setEditingGraduate] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [selectedCourseExamBody, setSelectedCourseExamBody] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Filters
  const [filters, setFilters] = useState({
    searchTerm: '',
    department_id: '',
    course_id: '',
    level_id: ''
  });

  const [formData, setFormData] = useState({
    admin_number: '',
    name: '',
    telephone: '',
    email: '',
    department_id: '',
    course_id: '',
    graduation_day_id: '',
    grade: ''
  });

  // Permissions
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const canDelete = user?.role === 'admin' || user?.role === 'editor';
  const canAdd = user?.role === 'admin' || user?.role === 'editor';
  const isViewer = user?.role === 'viewer';

  // Duplicate check (client-side, by digits)
  const isDuplicateAdminNumber = useCallback(
    (adminNumber, excludeId = null) => {
      const digits = extractAdminNumberDigits(
        normalizeAdminNumber(adminNumber)
      );
      if (!digits) return false;
      return graduates.some((g) => {
        if (excludeId && g.id === excludeId) return false;
        const existingDigits = extractAdminNumberDigits(
          normalizeAdminNumber(g.admin_number)
        );
        return existingDigits === digits;
      });
    },
    [graduates]
  );

  const getGradeOptions = (examBodyCode) => {
    switch (examBodyCode) {
      case 'TVET-CDACC':
        return [
          { value: 'Mastery', label: 'Mastery' },
          { value: 'Proficiency', label: 'Proficiency' },
          { value: 'Competent', label: 'Competent' }
        ];
      case 'KNEC':
        return [
          { value: 'Distinction', label: 'Distinction' },
          { value: 'Credit', label: 'Credit' },
          { value: 'Pass', label: 'Pass' }
        ];
      case 'NITA':
        return [
          { value: 'First Class', label: 'First Class' },
          { value: 'Pass', label: 'Pass' }
        ];
      default:
        return [
          { value: 'Distinction', label: 'Distinction' },
          { value: 'Credit', label: 'Credit' },
          { value: 'Pass', label: 'Pass' }
        ];
    }
  };

  const getExamBodyForCourse = (courseId) => {
    const course = allCourses.find((c) => c.id === parseInt(courseId));
    if (course && course.exam_body_id) {
      return examBodies.find((eb) => eb.id === course.exam_body_id);
    }
    return null;
  };

  const uniqueLevels = useMemo(() => {
    const levelMap = new Map();
    allCourses.forEach((course) => {
      if (course.level_id && !levelMap.has(course.level_id)) {
        levelMap.set(course.level_id, {
          id: course.level_id,
          level_number: course.level_number,
          level_name: course.level_name,
          display_name: course.level_number
            ? `Level ${course.level_number}`
            : course.level_name
        });
      }
    });
    return Array.from(levelMap.values()).sort((a, b) => {
      if (a.level_number && b.level_number)
        return a.level_number - b.level_number;
      if (a.level_number) return -1;
      if (b.level_number) return 1;
      return (a.level_name || '').localeCompare(b.level_name || '');
    });
  }, [allCourses]);

  const availableCourses = useMemo(() => {
    let courses = allCourses;
    if (filters.department_id) {
      courses = allCourses.filter(
        (course) => course.department_id === parseInt(filters.department_id)
      );
    }
    return [...courses].sort((a, b) => a.name.localeCompare(b.name));
  }, [allCourses, filters.department_id]);

  const availableDepartments = useMemo(() => {
    if (user?.role === 'editor' && user?.department_id) {
      return departments.filter(
        (dept) => dept.id === parseInt(user.department_id)
      );
    }
    return departments;
  }, [departments, user]);

  useEffect(() => {
    if (formData.course_id) {
      const examBody = getExamBodyForCourse(formData.course_id);
      setSelectedCourseExamBody(examBody);
      if (examBody && examBody.code !== selectedCourseExamBody?.code) {
        setFormData((prev) => ({ ...prev, grade: '' }));
      }
    } else {
      setSelectedCourseExamBody(null);
    }
  }, [formData.course_id, allCourses, examBodies]);

  const applyFilters = useCallback(() => {
    let filtered = [...graduates];

    if (user?.role === 'editor' && user?.department_id) {
      filtered = filtered.filter(
        (grad) => grad.department_id === parseInt(user.department_id)
      );
    }

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (grad) =>
          grad.admin_number?.toLowerCase().includes(searchLower) ||
          grad.full_name?.toLowerCase().includes(searchLower) ||
          grad.telephone?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.department_id) {
      filtered = filtered.filter(
        (grad) => grad.department_id === parseInt(filters.department_id)
      );
    }

    if (filters.course_id) {
      filtered = filtered.filter(
        (grad) => grad.course_id === parseInt(filters.course_id)
      );
    }

    if (filters.level_id) {
      filtered = filtered.filter(
        (grad) => grad.level_id === parseInt(filters.level_id)
      );
    }

    setFilteredGraduates(filtered);
    setCurrentPage(1);
  }, [graduates, filters, user]);

  const fetchData = useCallback(async () => {
    if (dataFetched) return;
    setInitialLoading(true);
    try {
      const [gradsRes, deptsRes, coursesRes, gradDaysRes, examRes] =
        await Promise.all([
          api.get('/graduates'),
          api.get('/departments'),
          api.get('/courses'),
          api.get('/graduation-days'),
          api.get('/exam-bodies')
        ]);

      let graduatesData = gradsRes.data;
      if (user?.role === 'editor' && user?.department_id) {
        graduatesData = graduatesData.filter(
          (g) => g.department_id === parseInt(user.department_id)
        );
      }

      graduatesData = graduatesData.map((g) => ({
        ...g,
        admin_number: normalizeAdminNumber(g.admin_number)
      }));

      setGraduates(graduatesData);
      setFilteredGraduates(graduatesData);
      setDepartments(deptsRes.data);
      setAllCourses(coursesRes.data);
      setGraduationDays(gradDaysRes.data);
      setExamBodies(examRes.data);
      setDataFetched(true);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  }, [user, dataFetched]);

  useEffect(() => {
    if (!dataFetched) fetchData();
  }, [fetchData, dataFetched]);

  useEffect(() => {
    const timeoutId = setTimeout(() => applyFilters(), 300);
    return () => clearTimeout(timeoutId);
  }, [applyFilters]);

  // Escape key + body scroll lock while modal is open
  useEffect(() => {
    if (!showForm) return;

    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') closeForm();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      department_id: '',
      course_id: '',
      level_id: ''
    });
  };

  useEffect(() => {
    if (formData.department_id) {
      const filtered = allCourses.filter(
        (course) => course.department_id === parseInt(formData.department_id)
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses([]);
    }
  }, [formData.department_id, allCourses]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredGraduates.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredGraduates.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({
      admin_number: '',
      name: '',
      telephone: '',
      email: '',
      department_id: '',
      course_id: '',
      graduation_day_id: '',
      grade: ''
    });
    setEditingGraduate(null);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (graduate) => {
    if (!canEdit) {
      toast.error('You do not have permission to edit graduates');
      return;
    }
    setEditingGraduate(graduate);
    setFormData({
      admin_number: normalizeAdminNumber(graduate.admin_number),
      name: graduate.full_name || graduate.name || '',
      telephone: graduate.telephone || '',
      email: graduate.email || '',
      department_id: graduate.department_id,
      course_id: graduate.course_id,
      graduation_day_id: graduate.graduation_day_id || '',
      grade: graduate.grade || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      toast.error('You do not have permission to delete graduates');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this graduate?'))
      return;

    setLoading(true);
    try {
      await api.delete(`/graduates/${id}`);
      toast.success('Graduate deleted successfully');
      setDataFetched(false);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Error deleting graduate'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canAdd) {
      toast.error('You do not have permission to add graduates');
      return;
    }

    if (
      !formData.admin_number ||
      !formData.name ||
      !formData.department_id ||
      !formData.course_id
    ) {
      toast.error('Please fill all required fields');
      return;
    }

    const normalizedAdmin = normalizeAdminNumber(formData.admin_number);

    const excludeId = editingGraduate?.id || null;
    if (isDuplicateAdminNumber(normalizedAdmin, excludeId)) {
      toast.error(
        `Duplicate: admin number with digits "${extractAdminNumberDigits(
          normalizedAdmin
        )}" already exists.`
      );
      return;
    }

    setLoading(true);
    try {
      const selectedCourse = allCourses.find(
        (c) => c.id === parseInt(formData.course_id)
      );

      const graduateData = {
        ...formData,
        admin_number: normalizedAdmin,
        level_id: selectedCourse?.level_id,
        exam_body_id: selectedCourse?.exam_body_id
      };

      if (editingGraduate) {
        await api.put(`/graduates/${editingGraduate.id}`, graduateData);
        toast.success('Graduate updated successfully');
      } else {
        await api.post('/graduates', graduateData);
        toast.success('Graduate added successfully');
      }

      closeForm();
      setDataFetched(false);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Error saving graduate'
      );
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentGradeOptions = selectedCourseExamBody
    ? getGradeOptions(selectedCourseExamBody.code)
    : getGradeOptions(null);

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Graduates Management
        </h1>
        {canAdd && !isViewer && (
          <div className="flex gap-3 w-full sm:w-auto flex-wrap">
            <div className="w-full sm:w-80">
              <Input
                label=""
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleFilterChange}
                placeholder="🔍 Search by Admin No, Name, or Mobile..."
                className="w-full h-10 px-4"
              />
            </div>
            <Button
              onClick={() => setShowCSVModal(true)}
              className="whitespace-nowrap h-10 px-4 flex items-center justify-center bg-green-600 hover:bg-green-700"
            >
              📤 Import CSV
            </Button>
            <Button
              onClick={openAddForm}
              className="whitespace-nowrap h-10 px-4 flex items-center justify-center"
            >
              + Add Graduate
            </Button>
          </div>
        )}
        {isViewer && (
          <div className="w-full sm:w-80">
            <Input
              label=""
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleFilterChange}
              placeholder="🔍 Search by Admin No, Name, or Mobile..."
              className="w-full h-10 px-4"
            />
          </div>
        )}
      </div>

      {/* Filters Row */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">
              Department
            </label>
            <select
              className="input-field w-full h-10 max-w-full truncate"
              name="department_id"
              value={filters.department_id}
              onChange={handleFilterChange}
              disabled={user?.role === 'editor'}
              title={
                filters.department_id
                  ? availableDepartments.find(
                      (d) => String(d.id) === String(filters.department_id)
                    )?.name || ''
                  : 'All Departments'
              }
            >
              <option value="">All Departments</option>
              {availableDepartments.map((dept) => (
                <option key={dept.id} value={dept.id} title={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>
            {user?.role === 'editor' && (
              <p className="text-xs text-gray-500 mt-1">
                Filtered by your department
              </p>
            )}
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">
              Course
            </label>
            <CourseSelect
              courses={availableCourses}
              value={filters.course_id}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, course_id: val }))
              }
              placeholder="All Courses"
            />
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">
              Level
            </label>
            <select
              className="input-field w-full h-10"
              name="level_id"
              value={filters.level_id}
              onChange={handleFilterChange}
            >
              <option value="">All Levels</option>
              {uniqueLevels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.display_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full h-10 flex items-center justify-center text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* CSV Upload Modal */}
      <CSVUploadModal
        isOpen={showCSVModal}
        onClose={() => setShowCSVModal(false)}
        onUploadComplete={() => {
          setDataFetched(false);
          fetchData();
        }}
      />

      {/* Results Count */}
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-gray-600">
          Showing {indexOfFirstItem + 1} to{' '}
          {Math.min(indexOfLastItem, filteredGraduates.length)} of{' '}
          {filteredGraduates.length} graduates
        </p>
      </div>

      {/* Graduates Table */}
      <div className="overflow-x-auto shadow rounded-lg">
        <table className="min-w-[1200px] bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">
                #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Admin No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Full Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Telephone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Course &amp; Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Exam Body
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Graduation Day
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Grade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentItems.length === 0 ? (
              <tr>
                <td
                  colSpan="11"
                  className="px-6 py-4 text-center text-gray-500"
                >
                  No graduates found.
                </td>
              </tr>
            ) : (
              currentItems.map((grad, index) => (
                <tr
                  key={grad.id}
                  className={`hover:bg-gray-50 ${
                    editingGraduate?.id === grad.id
                      ? 'bg-yellow-50 ring-2 ring-yellow-300'
                      : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 sticky left-0 bg-white">
                    {indexOfFirstItem + index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {normalizeAdminNumber(grad.admin_number)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">
                      {grad.full_name || grad.name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {grad.telephone || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {grad.email || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {grad.department_name || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {grad.course_name
                        ? `${grad.course_name}${
                            grad.level_number
                              ? ` (Level ${grad.level_number})`
                              : grad.level_name
                              ? ` (${grad.level_name})`
                              : ''
                          }`
                        : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {grad.exam_body_name || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {grad.ceremony_date
                        ? new Date(grad.ceremony_date).toLocaleDateString()
                        : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        grad.grade === 'Distinction'
                          ? 'bg-yellow-100 text-yellow-800'
                          : grad.grade === 'Credit'
                          ? 'bg-blue-100 text-blue-800'
                          : grad.grade === 'Pass'
                          ? 'bg-green-100 text-green-800'
                          : grad.grade === 'Mastery'
                          ? 'bg-purple-100 text-purple-800'
                          : grad.grade === 'Proficiency'
                          ? 'bg-indigo-100 text-indigo-800'
                          : grad.grade === 'Competent'
                          ? 'bg-cyan-100 text-cyan-800'
                          : grad.grade === 'First Class'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {grad.grade || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(grad)}
                        className="text-primary hover:text-primary-dark mr-3"
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(grad.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                    {isViewer && (
                      <span className="text-gray-400 text-xs">
                        View Only
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            Previous
          </button>

          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded ${
                    currentPage === pageNum
                      ? 'bg-secondary text-gray-900 font-semibold'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            Next
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          ADD/EDIT GRADUATE — FLOATING FORM MODAL
          - No backdrop tint, no blur
          - Course field uses a custom dropdown
            * Option rows show: Course Name (line 1) + Level (line 2)
            * Long names wrap; no truncation
            * Built-in search box
          ══════════════════════════════════════════════════════ */}
      {showForm && canAdd && (
        <div
          className="absolute inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-24"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 rounded-t-lg flex-shrink-0 bg-white">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingGraduate ? 'Edit Graduate' : 'Add New Graduate'}
              </h2>
              <button
                type="button"
                onClick={closeForm}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-visible"
            >
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Admin Number"
                    value={formData.admin_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        admin_number: e.target.value.toUpperCase()
                      })
                    }
                    onBlur={(e) =>
                      setFormData({
                        ...formData,
                        admin_number: normalizeAdminNumber(e.target.value)
                      })
                    }
                    placeholder="e.g., BTL4/4566/S2024"
                    required
                    className="uppercase"
                  />
                  <Input
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., John Malusi"
                    required
                  />
                  <Input
                    label="Telephone"
                    value={formData.telephone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        telephone: e.target.value
                      })
                    }
                    placeholder="e.g., 0712345678"
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="john@example.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="mb-4">
                    <label className="label">Department</label>
                    <select
                      className="input-field w-full max-w-full truncate"
                      value={formData.department_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          department_id: e.target.value,
                          course_id: ''
                        })
                      }
                      required
                      disabled={user?.role === 'editor'}
                      title={
                        formData.department_id
                          ? availableDepartments.find(
                              (d) =>
                                String(d.id) === String(formData.department_id)
                            )?.name || ''
                          : '-- Select Department --'
                      }
                    >
                      <option value="">-- Select Department --</option>
                      {availableDepartments.map((dept) => (
                        <option
                          key={dept.id}
                          value={dept.id}
                          title={dept.name}
                        >
                          {dept.name}
                        </option>
                      ))}
                    </select>
                    {user?.role === 'editor' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Department assigned to your account
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="label">Course</label>
                    <CourseSelect
                      courses={filteredCourses}
                      value={formData.course_id}
                      onChange={(val) =>
                        setFormData({ ...formData, course_id: val })
                      }
                      disabled={!formData.department_id}
                      placeholder={
                        formData.department_id
                          ? '-- Select Course --'
                          : '-- Select Department first --'
                      }
                    />
                    {!formData.department_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Please select a department first
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="label">Graduation Day</label>
                    <select
                      className="input-field w-full max-w-full truncate"
                      value={formData.graduation_day_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          graduation_day_id: e.target.value
                        })
                      }
                    >
                      <option value="">-- Select Graduation Day --</option>
                      {graduationDays.map((day) => (
                        <option key={day.id} value={day.id}>
                          {new Date(day.ceremony_date).toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="label">Grade</label>
                    <select
                      className="input-field w-full max-w-full truncate"
                      value={formData.grade}
                      onChange={(e) =>
                        setFormData({ ...formData, grade: e.target.value })
                      }
                      disabled={!selectedCourseExamBody}
                    >
                      <option value="">-- Select Grade --</option>
                      {currentGradeOptions.map((grade) => (
                        <option key={grade.value} value={grade.value}>
                          {grade.label}
                        </option>
                      ))}
                    </select>
                    {!selectedCourseExamBody && formData.course_id && (
                      <p className="text-xs text-amber-600 mt-1">
                        Loading grade options...
                      </p>
                    )}
                    {!formData.course_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Select a course first to see grade options
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
                <Button type="button" variant="outline" onClick={closeForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading
                    ? 'Saving...'
                    : editingGraduate
                    ? 'Update Graduate'
                    : 'Save Graduate'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Graduates;
